import { useState } from 'react';
import { useClassPerfSyllabus, useDeleteSyllabusEntry } from '../../../api/hooks';
import { useAuth } from '../../../../utils/Auth/AuthProvider';
import { Button, ConfirmDialog, EmptyState, PanelCard } from '../../../components/ui';
import Spinner from '../../../components/ui/Spinner';
import ErrorPanel from '../../../components/ui/ErrorPanel';
import { FaClipboardList } from '../../../components/ui/icons';
import WeeklySyllabusList from '../components/WeeklySyllabusList';
import SyllabusEntryForm from '../components/SyllabusEntryForm';

interface Props {
  gradeId: number;
  sectionId: number | 'all';
  weeks?: number;
  /** "Grade 10 · Section B", for the hint under the title. */
  ctx?: string;
  /** Offered by the empty state's "Show Section B" button. */
  suggestSection?: { id: number; name: string };
  onPickSection?: (id: number) => void;
}

export default function SyllabusTab({ gradeId, sectionId, weeks, ctx, suggestSection, onPickSection }: Props) {
  const { role } = useAuth();
  const canWrite = role === 'school_admin' || role === 'school' || role === 'admin' || role === 'teacher';
  const [adding, setAdding] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const deleteMut = useDeleteSyllabusEntry();
  const q = useClassPerfSyllabus({ grade: gradeId, section: sectionId, weeks });

  if (sectionId === 'all') {
    return (
      <PanelCard>
        <EmptyState icon={<FaClipboardList />} title="Pick a section"
          body="The weekly syllabus is kept per section. Choose one above to see what was taught each week."
          action={suggestSection && onPickSection
            ? <Button variant="ghost" onClick={() => onPickSection(suggestSection.id)}>{`Show Section ${suggestSection.name}`}</Button>
            : undefined} />
      </PanelCard>
    );
  }
  if (q.isLoading) return <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  if (q.isError || !q.data) return <ErrorPanel />;
  return (
    <>
      <PanelCard title="What was taught each week" align="end"
        subtitle={`${ctx ? `${ctx} · ` : ''}teacher entries override plans inferred from student study`}>
        <WeeklySyllabusList
          data={q.data}
          canWrite={canWrite}
          onAddEntry={(w) => setAdding(w.week_start)}
          onDeleteEntry={(id) => setPendingDelete(id)} />
      </PanelCard>
      {adding && (
        <SyllabusEntryForm
          gradeId={gradeId}
          sectionId={sectionId as number}
          defaultWeek={adding}
          onClose={() => setAdding(null)} />
      )}
      <ConfirmDialog
        isOpen={pendingDelete != null}
        title="Delete syllabus entry?"
        body="This removes the entry for every viewer."
        confirmLabel="Delete"
        onConfirm={() => { if (pendingDelete != null) deleteMut.mutate(pendingDelete); setPendingDelete(null); }}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
